import React, { useEffect, useRef, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const POLL_MS = 900;

function ContextPicker({ brands, products, audiences, onStart, starting }) {
  const [brandId, setBrandId] = useState("");
  const [productId, setProductId] = useState("");
  const [audienceId, setAudienceId] = useState("");

  // Populate defaults once the parent's fetches resolve.
  useEffect(() => { if (!brandId && brands[0]?.id) setBrandId(brands[0].id); }, [brands, brandId]);
  useEffect(() => { if (!productId && products[0]?.id) setProductId(products[0].id); }, [products, productId]);
  useEffect(() => { if (!audienceId && audiences[0]?.id) setAudienceId(audiences[0].id); }, [audiences, audienceId]);

  return (
    <form
      className="bo-card"
      onSubmit={(e) => { e.preventDefault(); onStart({ brand_id: brandId || null, product_id: productId || null, audience_id: audienceId || null }); }}
      data-testid="iv-context-form"
    >
      <div className="bo-eyebrow">Interview context</div>
      <h2 className="bo-card-title">Choose the Brains this interview will build</h2>
      <p className="bo-card-lede">
        Pick any subset. Questions are scored by information gain against the current state of each Brain.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginTop: 10 }}>
        <label>
          <div className="bo-eyebrow">Brand</div>
          <select className="bo-input" value={brandId} onChange={e => setBrandId(e.target.value)} data-testid="iv-brand-select">
            <option value="">(none)</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label>
          <div className="bo-eyebrow">Product</div>
          <select className="bo-input" value={productId} onChange={e => setProductId(e.target.value)} data-testid="iv-product-select">
            <option value="">(none)</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>
          <div className="bo-eyebrow">Audience</div>
          <select className="bo-input" value={audienceId} onChange={e => setAudienceId(e.target.value)} data-testid="iv-audience-select">
            <option value="">(none)</option>
            {audiences.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
      </div>
      <button type="submit" className="bo-btn bo-btn-primary" disabled={starting} style={{ marginTop: 12 }} data-testid="iv-start-btn">
        {starting ? "Starting…" : "Start interview"}
      </button>
    </form>
  );
}

function QuestionCard({ question, phrasing, onAnswer, onSkip, saving }) {
  const [answer, setAnswer] = useState("");
  useEffect(() => { setAnswer(""); }, [question?.id]);

  if (!question) {
    return (
      <EmptyState
        title="Interview complete"
        description="Every seed question has been answered or skipped. Return later as more Brains are filled in."
        testId="iv-empty-done"
      />
    );
  }

  const displayText = phrasing?.rephrased || question.template;
  const isPhrasing = !phrasing;
  const isList = question.value_type === "list";

  const submit = (e) => {
    e.preventDefault();
    if (!answer.trim()) return;
    let value = answer.trim();
    if (isList) value = value.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    onAnswer(value);
  };

  return (
    <form className="bo-card" onSubmit={submit} data-testid={`iv-question-${question.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div className="bo-eyebrow">Question · {question.scope} · info gain {Math.round(question.info_gain)}</div>
        <Pill tone={phrasing?.fallback ? "warn" : "muted"}>
          {isPhrasing ? "phrasing…" : (phrasing.fallback ? "template (fallback)" : "warmed by llm")}
        </Pill>
      </div>
      <h2 className="bo-card-title" style={{ marginTop: 6 }} data-testid="iv-question-text">
        {displayText}
      </h2>
      {question.help_hint && (
        <p style={{ color: "var(--bo-muted)", fontSize: 13, marginTop: 4 }}>{question.help_hint}</p>
      )}
      <textarea
        className="bo-input"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder={isList ? "Comma or newline separated" : "Your answer…"}
        rows={isList ? 4 : 3}
        style={{ marginTop: 10 }}
        required
        data-testid="iv-answer-input"
      />
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button type="submit" className="bo-btn bo-btn-primary" disabled={saving || !answer.trim()} data-testid="iv-submit-btn">
          {saving ? "Saving…" : "Submit answer"}
        </button>
        <button type="button" className="bo-btn bo-btn-ghost" onClick={onSkip} disabled={saving} data-testid="iv-skip-btn">
          Skip
        </button>
      </div>
      <p style={{ marginTop: 10, fontSize: 12, color: "var(--bo-muted)" }}>
        Answers land in the Review Queue as pending proposals — nothing enters the Brain without human review.
      </p>
    </form>
  );
}

export default function Interview() {
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [question, setQuestion] = useState(null);
  const [phrasing, setPhrasing] = useState(null);   // {rephrased, fallback, cost_usd}
  const [saving, setSaving] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);

  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [b, p, a] = await Promise.all([
          api.get("/brands"),
          api.get("/products"),
          api.get("/audiences"),
        ]);
        setBrands(b.data.brands || []);
        setProducts(p.data.products || []);
        setAudiences(a.data.audiences || []);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startSession = async (ctx) => {
    setStarting(true); setError(null);
    try {
      const r = await api.post("/intelligence/interview/sessions", ctx);
      setSession(r.data.session);
      await loadNext(r.data.session);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setStarting(false);
    }
  };

  const loadNext = async (sess) => {
    setLoadingNext(true);
    setPhrasing(null);
    setQuestion(null);
    try {
      const r = await api.get(`/intelligence/interview/sessions/${sess.id}/next`);
      setQuestion(r.data.question);
      const jobId = r.data.phrase_job_id;
      if (jobId && r.data.question) {
        // Poll the phrase job.
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const j = await api.get(`/intelligence/jobs/${jobId}`);
            const status = j.data.job?.status;
            if (status === "succeeded" || status === "failed") {
              clearInterval(pollRef.current);
              pollRef.current = null;
              const res = j.data.job?.result || {};
              setPhrasing({
                rephrased: res.rephrased || r.data.question.template,
                fallback: !!res.fallback,
                cost_usd: res.cost_usd || 0,
              });
            }
          } catch (_) { /* keep polling */ }
        }, POLL_MS);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoadingNext(false);
    }
  };

  const onAnswer = async (value) => {
    if (!session || !question) return;
    setSaving(true); setError(null);
    try {
      await api.post(
        `/intelligence/interview/sessions/${session.id}/answer`,
        { question_id: question.id, answer_value: value }
      );
      await loadNext(session);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onSkip = async () => {
    if (!session || !question) return;
    setSaving(true); setError(null);
    try {
      await api.post(
        `/intelligence/interview/sessions/${session.id}/skip`,
        { question_id: question.id }
      );
      await loadNext(session);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell title="Adaptive Interview">
      <div className="bo-eyebrow" data-testid="iv-eyebrow">Gate 2 · Slice 5a · Adaptive Interview</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }} data-testid="iv-title">
        Adaptive Interview
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, margin: "0 0 20px 0" }} data-testid="iv-lede">
        The interview asks the highest-value question at any moment, warmed by Sonnet 4.5. Answers become
        proposals in the Review Queue — nothing enters the Brain without human review.
      </p>

      {loading && <LoadingCard label="Loading context…" />}
      <ErrorBanner error={error} />

      {!loading && !session && (
        <ContextPicker
          brands={brands}
          products={products}
          audiences={audiences}
          onStart={startSession}
          starting={starting}
        />
      )}

      {session && loadingNext && <LoadingCard label="Finding your next question…" />}
      {session && !loadingNext && (
        <QuestionCard
          question={question}
          phrasing={phrasing}
          onAnswer={onAnswer}
          onSkip={onSkip}
          saving={saving}
        />
      )}
    </Shell>
  );
}
